import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import "../../assets/css/login.css";
import {
  Backdrop,
  CircularProgress,
  OutlinedInput,
  Typography,
  Button,
  Box,
  Grid,
  Paper,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { GetTimezoneInfoByTimezone } from "../../util/timezones";
import { SIGNUP_USER, SIGNUP_USER_GOOGLE } from "../../graphql/mutations";
import FloatingCompanyBrand from "../common/floatingCompanyBrand";
import GettingStartedGuide from "../common/gettingStartedGuide";

function SignUp(props) {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneId = GetTimezoneInfoByTimezone(timeZone)?.id;
  const searchParams = new URLSearchParams(window.location.search);
  const initialEmail = searchParams.get("email") || "";
  const pathname = window.location.pathname;
  const isTrial = pathname.includes("start-free-trial");

  const [email, setEmail] = useState(initialEmail);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const navigate = useNavigate();

  const [signUpUser] = useMutation(SIGNUP_USER);
  const [signUpWithGoogleMutation] = useMutation(SIGNUP_USER_GOOGLE);

  const handleClose = () => setOpen(false);

  const handleGuideOpen = () => setGuideOpen(true);
  const handleGuideClose = () => setGuideOpen(false);

  const signUpWithGoogle = useGoogleLogin({
    onSuccess: (response) => {
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${response.access_token}`,
          {
            headers: {
              Authorization: `Bearer ${response.access_token}`,
              Accept: "application/json",
            },
          }
        )
        .then((res) => {
          signUpWithGoogleMutation({
            variables: {
              email: res.data.email,
              username: res.data.name,
            },
          })
            .then((result) => {
              localStorage.setItem("token", result?.data?.signUpGoogle?.token);
              props.login(result?.data?.signUpGoogle?.token);
            })
            .catch((err) => {
              if (err.message === "Validation error") {
                setErrors(["Existing account with provided email"]);
              }
            });
        });
    },
    onFailure: (error) => console.log("Google sign-up failed:", error),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = [];

    if (!fullName.trim()) validationErrors.push("Full Name is required");
    if (!email.trim()) {
      validationErrors.push("Email is required");
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      validationErrors.push("Invalid Email Address");
    }
    if (!password.trim()) {
      validationErrors.push("Password is required");
    } else if (password.length < 6) {
      validationErrors.push("Password must be at least 6 characters long");
    }
    if (password !== passwordConfirm) {
      validationErrors.push("Passwords do not match");
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    signUpUser({
      variables: {
        email,
        password,
        username: fullName,
        timeZoneId: Number(timeZoneId),
      },
    })
      .then((result) => {
        setOpen(true);
        setTimeout(() => {
          handleClose();
          localStorage.setItem("token", result?.data?.signUp?.token);
          props.login(result?.data?.signUp?.token);
          navigate(`/login?email=${email}`);
        }, 500);
      })
      .catch((err) => setErrors([err.message]));
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      sx={{
        marginTop: {
          xs: isTrial ? "290px" : "90px",
          sm: isTrial ? "160px" : "50px",
        },
      }}
    >
      <FloatingCompanyBrand />
      {isTrial && (
        <Box mx="auto" maxWidth="600px" p={2} textAlign="center" mb={2}>
          <Typography variant="h4" gutterBottom>
            Try Plany365 for free
          </Typography>
          <Typography variant="h6">
            Sign up and register your business or (register your name as a business) today to receive a free{" "}
            <strong>30-day trial</strong> of the Standard plan. No credit card required.
          </Typography>
        </Box>
      )}
      <Box textAlign="center" mb={2}>
        <Link component="button" variant="body1" onClick={handleGuideOpen} style={{ textDecoration: "none" }}>
          Getting Started Guide
        </Link>
      </Box>

      <Paper elevation={3} style={{ padding: "30px", maxWidth: "400px", width: "100%" }}>
        <Typography variant="h5" align="center" gutterBottom>
          Create Your Account
        </Typography>
        <form onSubmit={handleSubmit} style={{ marginBottom: "20px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Confirm Password"
                type={showPasswordConfirm ? "text" : "password"}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswordConfirm((prev) => !prev)} edge="end">
                      {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
          </Grid>
          <Button variant="contained" color="primary" type="submit" fullWidth style={{ marginTop: "20px" }}>
            Sign Up
          </Button>
        </form>

        <Button variant="outlined" color="primary" fullWidth onClick={signUpWithGoogle} style={{ marginBottom: "20px" }}>
          Continue with Google
        </Button>

        {errors.length > 0 && (
          <Box>
            {errors.map((err, idx) => (
              <Typography key={idx} color="error" align="center">
                {err}
              </Typography>
            ))}
          </Box>
        )}

        <Backdrop open={open} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: "10px" }}>
            Redirecting to Login Page
          </Typography>
        </Backdrop>

        <Typography align="center" variant="body2">
          Already have an account? <a href="/login">Sign In</a>
        </Typography>
      </Paper>

      <GettingStartedGuide guideOpen={guideOpen} handleGuideClose={handleGuideClose} />
    </Box>
  );
}

export default SignUp;
